import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  cancelTrainingEvalRun,
  listAllTrainingEvalScenarios,
  type TrainingEvalMetricsView,
  type TrainingEvalRunProgressView,
  type TrainingEvalRunSummaryView,
  type TrainingEvalRunView,
  type TrainingEvalScenarioView,
  type TrainingEvalSuccessTermsView,
  type TrainingEvalVideoView,
  type TrainingImageMetadataView,
  type TrainingSessionPoliciesView,
} from "@/api/trainingEval";
import type { LoadState } from "@/components/dataset";
import { createLogger } from "@/logging";
import { isSucceededEvalStatus, resolveEvalLoadErrorMessage } from "@/modules/evaluation/public";
import { resolveRunExecution, resolveEvalScenario } from "./evalRunScenarioModel";
import {
  evictEvalRun,
  loadEvalRun,
  loadEvalRunMetrics,
  loadEvalRunProgress,
  loadEvalRunRecordedVideos,
  loadEvalRunSuccessTerms,
  loadSessionCheckpoints,
} from "./evalRunDataCache";
import { isLiveEvalStatus } from "./evalRunFormat";

import { imageMetadataState, useEvalImageMetadata } from "./useEvalImageMetadata";
import { useEvalBaseline } from "./useEvalBaseline";
import type { EvalBaselineState } from "./evalBaselineModel";

const NO_SCENARIOS: readonly TrainingEvalScenarioView[] = [];

const logger = createLogger("page/evaluationResults");
const DETAIL_POLL_MS = 5000;

export type EvalRunDetailController = {
  summary: TrainingEvalRunSummaryView;
  baseline: EvalBaselineState;
  detailState: LoadState<TrainingEvalRunView>;
  progressState: LoadState<TrainingEvalRunProgressView>;
  scenariosState: LoadState<readonly TrainingEvalScenarioView[]>;
  // 카탈로그에서 찾은 이 run 의 scenario 다. 카탈로그를 받기 전이거나, run 의 scenario 이름이
  // 카탈로그에 없으면 null 이다.
  scenario: TrainingEvalScenarioView | null;
  metadataState: LoadState<TrainingImageMetadataView | null>;
  policiesState: LoadState<TrainingSessionPoliciesView | null>;
  metricsState: LoadState<TrainingEvalMetricsView>;
  termsState: LoadState<TrainingEvalSuccessTermsView>;
  videosState: LoadState<readonly TrainingEvalVideoView[]>;
  effectiveStatus: string;
  isLive: boolean;
  canCancel: boolean;
  actionPending: boolean;
  actionError: string | null;
  cancel: () => void;
  // 실패한 조회만 다시 받는다. 받은 값은 그대로 둔다. 그래서 다른 섹션이 대기로 돌아가지 않는다.
  retry: () => void;
};

function resultState<T>(result: PromiseSettledResult<T>): LoadState<T> {
  return result.status === "fulfilled"
    ? { status: "success", data: result.value }
    : { status: "error", message: resolveEvalLoadErrorMessage(result.reason) };
}

export function useEvalRunDetailController(
  summary: TrainingEvalRunSummaryView,
  enabled: boolean,
): EvalRunDetailController {
  const [detailState, setDetailState] = useState<LoadState<TrainingEvalRunView>>({
    status: "idle",
  });
  const [progressState, setProgressState] = useState<LoadState<TrainingEvalRunProgressView>>({
    status: "idle",
  });
  const [scenariosState, setScenariosState] = useState<
    LoadState<readonly TrainingEvalScenarioView[]>
  >({
    status: "idle",
  });
  const [policiesState, setPoliciesState] = useState<LoadState<TrainingSessionPoliciesView | null>>(
    { status: "idle" },
  );
  const [metricsState, setMetricsState] = useState<LoadState<TrainingEvalMetricsView>>({
    status: "idle",
  });
  const [termsState, setTermsState] = useState<LoadState<TrainingEvalSuccessTermsView>>({
    status: "idle",
  });
  const [videosState, setVideosState] = useState<LoadState<readonly TrainingEvalVideoView[]>>({
    status: "idle",
  });
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const baselineSummaries = useMemo(() => [summary], [summary]);
  const baseline = useEvalBaseline(
    baselineSummaries,
    scenariosState.status === "success" ? scenariosState.data : NO_SCENARIOS,
    enabled,
  );

  const detail = detailState.status === "success" ? detailState.data : null;
  const progress = progressState.status === "success" ? progressState.data : null;
  const effectiveStatus = progress?.status ?? detail?.status ?? summary.status;
  const isLive = isLiveEvalStatus(effectiveStatus);
  const canCancel = isLive;

  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    setDetailState({ status: "loading" });
    setProgressState({ status: "loading" });

    async function tick(): Promise<void> {
      const [detailResult, progressResult] = await Promise.allSettled([
        loadEvalRun(summary.id),
        loadEvalRunProgress(summary.id),
      ]);
      if (disposed) return;
      setDetailState(resultState(detailResult));
      setProgressState(resultState(progressResult));
      if (detailResult.status === "rejected") {
        logger.error("loadEvalRun failed", detailResult.reason);
      }
      if (progressResult.status === "rejected") {
        logger.error("loadEvalRunProgress failed", progressResult.reason);
      }
      const status =
        progressResult.status === "fulfilled"
          ? progressResult.value.status
          : detailResult.status === "fulfilled"
            ? detailResult.value.status
            : summary.status;
      if (isLiveEvalStatus(status)) {
        timer = setTimeout(() => void tick(), DETAIL_POLL_MS);
      }
    }

    void tick();
    return () => {
      disposed = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [enabled, refreshKey, summary.id, summary.status]);

  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    setScenariosState({ status: "loading" });
    setPoliciesState(
      summary.source_session_id === null
        ? { status: "success", data: null }
        : { status: "loading" },
    );
    const policiesRequest =
      summary.source_session_id === null
        ? Promise.resolve<TrainingSessionPoliciesView | null>(null)
        : loadSessionCheckpoints(summary.source_session_id);
    void Promise.allSettled([listAllTrainingEvalScenarios(), policiesRequest]).then(
      ([scenariosResult, policiesResult]) => {
        if (disposed) return;
        setScenariosState(resultState(scenariosResult));
        setPoliciesState(resultState(policiesResult));
        if (scenariosResult.status === "rejected") {
          logger.error("listAllTrainingEvalScenarios failed", scenariosResult.reason);
        }
        if (policiesResult.status === "rejected") {
          logger.error("loadSessionCheckpoints failed", policiesResult.reason);
        }
      },
    );
    return () => {
      disposed = true;
    };
  }, [enabled, summary.id, summary.source_session_id]);

  const scenario = useMemo(() => {
    if (scenariosState.status !== "success") return null;
    return resolveEvalScenario(scenariosState.data, detail?.scenario ?? summary.scenario);
  }, [detail?.scenario, scenariosState, summary.scenario]);
  const execution = resolveRunExecution(summary, detail, scenario);
  const { states: metadataStates, retry: retryMetadata } = useEvalImageMetadata(
    execution.tag === null ? [] : [execution.tag],
    enabled,
  );
  const metadataState = imageMetadataState(metadataStates, execution.tag);

  useEffect(() => {
    if (!enabled || !isSucceededEvalStatus(effectiveStatus)) {
      return;
    }
    let disposed = false;
    setMetricsState({ status: "loading" });
    setTermsState({ status: "loading" });
    setVideosState({ status: "loading" });
    void Promise.allSettled([
      loadEvalRunMetrics(summary.id),
      loadEvalRunSuccessTerms(summary.id),
      loadEvalRunRecordedVideos(summary.id),
    ]).then(([metricsResult, termsResult, videosResult]) => {
      if (disposed) return;
      setMetricsState(resultState(metricsResult));
      setTermsState(resultState(termsResult));
      setVideosState(resultState(videosResult));
      if (metricsResult.status === "rejected") {
        logger.error("loadEvalRunMetrics failed", metricsResult.reason);
      }
      if (termsResult.status === "rejected") {
        logger.error("loadEvalRunSuccessTerms failed", termsResult.reason);
      }
      if (videosResult.status === "rejected") {
        logger.error("loadEvalRunRecordedVideos failed", videosResult.reason);
      }
    });
    return () => {
      disposed = true;
    };
  }, [effectiveStatus, enabled, summary.id]);

  const cancel = useCallback(() => {
    if (!canCancel) return;
    if (!window.confirm("이 Evaluation 실행을 취소할까요?")) return;
    setActionPending(true);
    setActionError(null);
    void cancelTrainingEvalRun(summary.id)
      .then((view) => {
        // run 을 바꾸는 mutation 은 취소뿐이다. 그래서 새로고침이 다시 읽기 전에 캐시 사본을 버린다.
        // 먼저 제거하면, 취소 전에 보낸 요청이 종료 상태인 것처럼 캐시에 저장되는 것도 막는다.
        evictEvalRun(summary.id);
        setDetailState({ status: "success", data: view });
        setRefreshKey((key) => key + 1);
      })
      .catch((error: unknown) => {
        logger.error("cancelTrainingEvalRun failed", error);
        setActionError(resolveEvalLoadErrorMessage(error));
      })
      .finally(() => setActionPending(false));
  }, [canCancel, summary.id]);

  const runId = summary.id;
  const sourceSessionId = summary.source_session_id;
  const retry = useCallback(() => {
    function reload<T>(
      state: LoadState<T>,
      setState: Dispatch<SetStateAction<LoadState<T>>>,
      label: string,
      request: () => Promise<T>,
    ): void {
      if (state.status !== "error") return;
      setState({ status: "loading" });
      void Promise.allSettled([request()]).then(([result]) => {
        setState(resultState(result));
        if (result.status === "rejected") logger.error(`${label} failed`, result.reason);
      });
    }
    reload(detailState, setDetailState, "loadEvalRun", () => loadEvalRun(runId));
    reload(metricsState, setMetricsState, "loadEvalRunMetrics", () => loadEvalRunMetrics(runId));
    reload(termsState, setTermsState, "loadEvalRunSuccessTerms", () =>
      loadEvalRunSuccessTerms(runId),
    );
    reload(videosState, setVideosState, "loadEvalRunRecordedVideos", () =>
      loadEvalRunRecordedVideos(runId),
    );
    reload(scenariosState, setScenariosState, "listAllTrainingEvalScenarios", () =>
      listAllTrainingEvalScenarios(),
    );
    if (sourceSessionId !== null) {
      reload(policiesState, setPoliciesState, "loadSessionCheckpoints", () =>
        loadSessionCheckpoints(sourceSessionId),
      );
    }
    if (
      metadataState.status === "error" ||
      (metadataState.status === "success" && metadataState.data === null)
    ) {
      retryMetadata();
    }
  }, [
    detailState,
    metadataState,
    metricsState,
    policiesState,
    runId,
    retryMetadata,
    scenariosState,
    sourceSessionId,
    termsState,
    videosState,
  ]);

  return {
    baseline,
    summary,
    detailState,
    progressState,
    scenariosState,
    scenario,
    metadataState,
    policiesState,
    metricsState,
    termsState,
    videosState,
    effectiveStatus,
    isLive,
    canCancel,
    actionPending,
    actionError,
    cancel,
    retry,
  };
}
